import type { useTranslate } from "@/lib/i18n";
import type { InvoiceItemFulfillment } from "@/lib/api/types";

type Translate = ReturnType<typeof useTranslate>;

/**
 * The reason codes a void can carry.
 *
 * The list the dialog offers for a VOID, and one of the two lists the shift
 * reviews read back. Lives beside the pricing helpers rather than in
 * `manage/lib` because the till is where a reason is MINTED — the back office
 * only ever reads one — and `manage` already imports from here
 * (`listSubtotal`).
 *
 * Mirrors the server's `VOID_REASON_CODES`
 * (`policies/void-reason.policy.ts`). The server now REFUSES a code from the
 * wrong list with a typed 400 (`RESTAURANT_REASON_CODE_INVALID`), so the two
 * lists here are not cosmetic: offering a void code on a comp sheet is a
 * guaranteed refusal at the till.
 */
export const VOID_REASON_CODES = [
  "CUSTOMER_CHANGED_MIND",
  "WRONG_ORDER",
  "QUALITY_ISSUE",
  "OTHER",
] as const;

/**
 * The reason codes a COMP can carry (B5, D44).
 *
 * A comp is not a void with a different button: a void is "this plate should
 * never have been on the bill", a comp is "it was, and the house is paying
 * for it". Nothing on the void list answers WHO the house paid for, which is
 * the only question a comp report is asked — hence its own vocabulary, and
 * `STAFF_MEAL` first, because staff food is the bucket owners actually chase
 * and the one the consumption report lifts out on its own line.
 *
 * Mirrors the server's `COMP_REASON_CODES` exactly. `OTHER` is deliberately
 * on both lists (and only `OTHER`), so the note gate below stays one rule.
 */
export const COMP_REASON_CODES = [
  "STAFF_MEAL",
  "GOODWILL",
  "KITCHEN_ERROR",
  "MANAGER_DISCRETION",
  "OTHER",
] as const;

/**
 * The reason codes a REFUND can carry (R10).
 *
 * The third vocabulary, and its own for the same reason the comp list is its
 * own: nothing on the void or comp lists answers WHY money that had already
 * been taken went back. Mirrors the server's `REFUND_REASON_CODES`
 * (`policies/refund-reason.policy.ts`) exactly — a code from the wrong list is
 * a typed 400, so these are not cosmetic.
 *
 * `OTHER` is on all three lists, and only `OTHER`, so the note gate stays one
 * rule.
 */
export const REFUND_REASON_CODES = [
  "WRONG_CHARGE",
  "QUALITY",
  "DUPLICATE_CHARGE",
  "CHARGEBACK",
  // Founder-approved 2026-09-09. `GOODWILL` is the manager buying peace, which
  // landed in `OTHER` every time and made `OTHER` unreadable — an owner could
  // not tell a failing kitchen from a generous floor manager. `WRONG_ITEM` is
  // being served something not ordered: not `WRONG_CHARGE` (priced wrong), not
  // `QUALITY` (right dish, badly made), and newly necessary because a guest
  // served the wrong SUBSTITUTION (D61) had no honest code at all.
  //
  // `GOODWILL` is deliberately the SAME word the comp list uses: one word, one
  // meaning, so "what did we give away for goodwill" is one question.
  "GOODWILL",
  "WRONG_ITEM",
  "OTHER",
] as const;

/**
 * The reason codes a REMAKE can carry (B117 round table 2, Q4).
 *
 * Its own list, mirroring the server's `REMAKE_REASON_CODES`, for the reason
 * D44 gave void and comp separate ones: the vocabulary belongs to the DECISION.
 * A void's codes are mostly about the customer, so nobody has to own them; a
 * remake's cannot be written that way and stay useful, because telling the
 * floor's mistake from the kitchen's is the entire point of asking.
 *
 * Two conditions the round table attached, and they are why this is safe to
 * ship: it attributes to a STATION rather than a named person, and it rolls up
 * BY DISH and period only. "The day someone builds a remakes-by-waiter league
 * table off this field, my section stops sending food back and starts serving
 * cold fish to keep its number clean."
 */
export const REMAKE_REASON_CODES = [
  "GUEST_CHANGED_MIND",
  "WRONG_ITEM_ORDERED",
  "COOKED_WRONG",
  "DAMAGED_OR_COLD",
  "OTHER",
] as const;

export type VoidReasonCode = (typeof VOID_REASON_CODES)[number];
export type CompReasonCode = (typeof COMP_REASON_CODES)[number];
export type RefundReasonCode = (typeof REFUND_REASON_CODES)[number];

export type RemakeReasonCode = (typeof REMAKE_REASON_CODES)[number];

/** Every code any vocabulary can mint. `OTHER` belongs to all four. */
export type ReasonCode =
  | VoidReasonCode
  | CompReasonCode
  | RefundReasonCode
  | RemakeReasonCode;

/**
 * Which decision is being filed. The dialog takes it as a prop, the shift
 * reviews derive it from the command type, and every function below that
 * needs to know WHICH list applies takes it rather than guessing.
 */
export type ReasonKind = "void" | "comp" | "refund" | "remake";

/** The codes offered for one kind — what the dialog's picker is built from. */
export function reasonCodesForKind(kind: ReasonKind): readonly ReasonCode[] {
  if (kind === "comp") return COMP_REASON_CODES;
  if (kind === "refund") return REFUND_REASON_CODES;
  if (kind === "remake") return REMAKE_REASON_CODES;
  return VOID_REASON_CODES;
}

/**
 * Matches the backend's `REASON_NOTE_MAX_LENGTH`
 * (`dto/invoice-command.dto.ts`) — a POS note is a sentence, not an essay.
 * Enforced here too so a cashier sees the limit while typing rather than
 * only on a server refusal.
 */
export const VOID_REASON_NOTE_MAX_LENGTH = 200;

/**
 * A reason code in words.
 *
 * An exhaustive `switch` with **no `default`**, deliberately: the compiler now
 * fails the next screen that adds a code without a sentence for it, which is
 * exactly the drift this was extracted to end. The dialog that mints these had
 * the only copy of it, so both shift reviews printed the raw enum —
 * `QUALITY_ISSUE` — at an owner reviewing last Friday's drawer.
 */
export function voidReasonLabel(t: Translate, code: ReasonCode): string {
  switch (code) {
    // B117/Q4 — the remake vocabulary. `GUEST_CHANGED_MIND` is its own word
    // rather than a reuse of CUSTOMER_CHANGED_MIND: the two lists are separate
    // by design and a shared constant would let one drift into the other.
    case "GUEST_CHANGED_MIND":
      return t((m) => m.pos.reasonGuestChangedMind);
    case "WRONG_ITEM_ORDERED":
      return t((m) => m.pos.reasonWrongItemOrdered);
    case "COOKED_WRONG":
      return t((m) => m.pos.reasonCookedWrong);
    case "DAMAGED_OR_COLD":
      return t((m) => m.pos.reasonDamagedOrCold);
    case "CUSTOMER_CHANGED_MIND":
      return t((m) => m.pos.reasonCustomerChangedMind);
    case "WRONG_ORDER":
      return t((m) => m.pos.reasonWrongOrder);
    case "QUALITY_ISSUE":
      return t((m) => m.pos.reasonQualityIssue);
    case "STAFF_MEAL":
      return t((m) => m.pos.reasonStaffMeal);
    case "GOODWILL":
      return t((m) => m.pos.reasonGoodwill);
    case "KITCHEN_ERROR":
      return t((m) => m.pos.reasonKitchenError);
    case "MANAGER_DISCRETION":
      return t((m) => m.pos.reasonManagerDiscretion);
    case "WRONG_CHARGE":
      return t((m) => m.pos.reasonWrongCharge);
    case "QUALITY":
      return t((m) => m.pos.reasonQuality);
    case "DUPLICATE_CHARGE":
      return t((m) => m.pos.reasonDuplicateCharge);
    case "CHARGEBACK":
      return t((m) => m.pos.reasonChargeback);
    // `GOODWILL` needs no arm of its own — it is already above, on the comp
    // list, and the switch is keyed on the CODE. That is the whole benefit of
    // reusing the word rather than minting `REFUND_GOODWILL`.
    case "WRONG_ITEM":
      return t((m) => m.pos.reasonWrongItem);
    case "OTHER":
      return t((m) => m.pos.reasonOther);
  }
}

/**
 * The same, for a code that arrived over the wire as a bare string.
 *
 * `ShiftException.reasonCode` is `string | null` — the command payload is not
 * a closed enum server-side — so a code this release has no word for prints
 * RAW rather than blank. An ugly `LATE_DELIVERY` still tells the reviewer
 * something; an empty chip tells them nothing and hides that a reason existed.
 */
export function voidReasonLabelForCode(
  t: Translate,
  code: string,
  kind: ReasonKind,
): string {
  if (isReasonCodeOfKind(code, kind)) return voidReasonLabel(t, code);
  // B5: a comp filed under a VOID code is history, never rewritten — the
  // server accepted those before it had two vocabularies, and the reports
  // bucket them as `LEGACY`. It is still a code this release has words for,
  // so the review prints the words. Falling through to the raw enum here
  // would punish the reader for a rule that came after the row.
  // A code filed under a DIFFERENT vocabulary is still a code this release has
  // words for — a comp filed under a void code is history the server accepted
  // before it had two lists, and a refund row is read by screens that ask for
  // the "void" kind. Falling through to the raw enum here would punish the
  // reader for a rule that came after the row.
  for (const other of REASON_KINDS) {
    if (other !== kind && isReasonCodeOfKind(code, other)) {
      return voidReasonLabel(t, code);
    }
  }
  // Anything else prints RAW rather than blank. `ShiftException.reasonCode`
  // is `string | null` on the wire, so a code a later release adds still
  // tells the reviewer something; an empty chip tells them nothing and hides
  // that a reason existed. Never a throw — this runs inside a print view.
  return code;
}

function isReasonCodeOfKind(
  code: string,
  kind: ReasonKind,
): code is ReasonCode {
  return (reasonCodesForKind(kind) as readonly string[]).includes(code);
}

/**
 * Which vocabulary an exception row's reason belongs to. `compItem` is the
 * only command that mints a comp code; every other exception type that
 * carries a reason mints a void one.
 */
export function reasonKindForExceptionType(type: string): ReasonKind {
  if (type === "compItem") return "comp";
  // R12: a refund files its reason against its OWN vocabulary, so a review
  // prints "الجودة" rather than the raw `QUALITY` at the one reader who cannot
  // ask the cashier what it meant.
  if (type === "refund") return "refund";
  return "void";
}

/** Every vocabulary, for the cross-kind fallback above. */
const REASON_KINDS: readonly ReasonKind[] = [
  "void",
  "comp",
  "refund",
  "remake",
];

/**
 * DEF-7's invariant, as one testable decision: given what is currently
 * selected and what the cashier has typed since, what should still be
 * selected?
 *
 * The void/comp dialog's reason field is a combobox over these four codes —
 * a picker, not a text field — but Headless UI's Combobox only calls its
 * `onChange` when an option is actually clicked or Enter-selected, never on
 * a keystroke that matches nothing. Typed prose that names none of the four
 * codes therefore left the previous selection sitting in state, unconfirmed
 * by anything on screen, and a Confirm tap sent that stale code out on the
 * command log — the bug this function exists to make impossible: it is
 * called on every keystroke, and a query that no longer names the current
 * selection returns `null` rather than leaving it in place.
 *
 * An empty query also returns `null` — no query means "show every option
 * again", which is not the same as "the current one is still chosen".
 */
/**
 * DEF-7 point 2, as a predicate: is there a real, cashier-chosen code to
 * confirm? `null` — nothing picked yet, or `nextReasonCodeForQuery` just
 * cleared a stale one — must never reach Confirm.
 */
export function canConfirmVoidReason(
  code: ReasonCode | null,
): code is ReasonCode {
  return code !== null;
}

/**
 * D43 (86cbdhg4p): `OTHER` says nothing about what happened — the one code
 * this dialog will not confirm without a note beside it. Mirrors the
 * backend's `OTHER_VOID_REASON_CODE` check
 * (`policies/void-reason-note.policy.ts`) so the two can never disagree
 * about which code this applies to.
 */
export function isVoidReasonNoteRequired(code: ReasonCode | null): boolean {
  return code === "OTHER";
}

/**
 * D43: the whole confirm gate, code AND note together — DEF-7's
 * `canConfirmVoidReason` plus the `OTHER`-requires-a-note rule this ticket
 * adds. `note` is the same trimmed value the field itself computes, so
 * whitespace-only text is treated as no note here too.
 */
export function canConfirmVoid(
  code: ReasonCode | null,
  note: string,
): code is ReasonCode {
  return (
    canConfirmVoidReason(code) &&
    (!isVoidReasonNoteRequired(code) || note.trim().length > 0)
  );
}

/**
 * D43 (86cbdhg4p) — the actual command payload the dialog's Confirm builds,
 * as one testable function rather than object-literal shape assembled
 * inline at the call site. `reasonNote` rides beside `reasonCode` (the
 * scope's whole point: additive, never a replacement) and is OMITTED, not
 * sent as `""`, when there is nothing to say — an absent field and an empty
 * string must mean the same thing on the wire, and there is no reason to
 * put the empty one there when `undefined` already says it.
 */
export function buildVoidCommandPayload(
  itemIds: string[],
  reasonCode: ReasonCode,
  reasonNote: string,
  /**
   * D141 — the cooked plates the cashier sent back to stock, and the
   * decision KIND they were sent back under.
   *
   * This builder is shared by the void AND the comp (one sheet, one
   * `VoidItemPayloadDto` on the server), and the server refuses
   * `returnToStockItemIds` on a comp in ANY non-null form — `[]` included —
   * with a typed 400 (`RESTAURANT_RETURN_TO_STOCK_NOT_ALLOWED`). So the key
   * is written only when `kind` is explicitly `"void"` AND at least one id
   * survives; a comp, a call without options, or an empty list produces
   * exactly the payload this function built before D141, byte for byte. The
   * ids are narrowed to `itemIds` (in `itemIds`' order, once each) because
   * the server answers anything else with `RESTAURANT_COMMAND_PAYLOAD_INVALID`.
   */
  returnToStock?: {
    kind: ReasonKind;
    itemIds: readonly string[];
  },
): {
  itemIds: string[];
  reasonCode: string;
  reasonNote?: string;
  returnToStockItemIds?: string[];
} {
  const trimmed = reasonNote.trim();
  const returned =
    returnToStock?.kind === "void"
      ? subsetInOrder(itemIds, returnToStock.itemIds)
      : [];
  return {
    itemIds,
    reasonCode,
    ...(trimmed ? { reasonNote: trimmed } : {}),
    ...(returned.length > 0 ? { returnToStockItemIds: returned } : {}),
  };
}

/**
 * D141 — the plate states in which the kitchen has cooked the food, so a void
 * or refund may send it back to stock.
 *
 * The till's mirror of the server's `RETURNABLE_FULFILLMENT_STATES`
 * (`policies/return-to-stock.policy.ts`): D20's own rule — "was it cooked" is
 * exactly "did it reach PREPARING". QUEUED never qualifies: nothing was made.
 * This is what the till KNOWS; the server re-checks against the depletion
 * ledger and answers `RESTAURANT_ITEM_NOT_RETURNABLE` with a `reason` when the
 * screen was stale (a remade plate back in QUEUED for its second cook, say) or
 * the plate was already returned. A cooked plate with no live cook rows — a
 * recipe-less dish, say — is skipped by the server, not refused (P10-D10):
 * nothing is written for it and the rest of the command applies.
 */
export const RETURNABLE_FULFILLMENT_STATES: readonly InvoiceItemFulfillment[] =
  ["PREPARING", "READY", "SERVED"];

export function isReturnableToStock(state: InvoiceItemFulfillment): boolean {
  return RETURNABLE_FULFILLMENT_STATES.includes(state);
}

/** D141 — the ids among `plates` the till believes were cooked, in order. */
export function returnableItemIds(
  plates: readonly { id: string; fulfillmentState: InvoiceItemFulfillment }[],
): string[] {
  return plates
    .filter((plate) => isReturnableToStock(plate.fulfillmentState))
    .map((plate) => plate.id);
}

/**
 * The refund command's payload (R1–R13, F17, D141), as one testable function
 * rather than an object literal at the call site — the same reason
 * `buildVoidCommandPayload` exists.
 *
 * Every optional field is OMITTED rather than sent empty, and in the order the
 * till always sent them: `reasonNote` only when there is one, `refundDeliveryFee`
 * only when the cashier ticked it (the server defaults it to false), and
 * `returnToStockItemIds` only when the cashier chose "returned to stock" and
 * at least one ticked plate survives the narrowing to `itemIds`. The default
 * refund — wasted, today's behaviour — is therefore the pre-D141 payload byte
 * for byte, which the specs pin with `JSON.stringify`.
 */
export function buildRefundCommandPayload(input: {
  expectedVersion: number;
  itemIds: string[];
  reasonCode: string;
  reasonNote: string;
  refundDeliveryFee: boolean;
  returnToStockItemIds?: readonly string[];
}): {
  expectedVersion: number;
  itemIds: string[];
  reasonCode: string;
  reasonNote?: string;
  refundDeliveryFee?: true;
  returnToStockItemIds?: string[];
} {
  const returned = subsetInOrder(
    input.itemIds,
    input.returnToStockItemIds ?? [],
  );
  return {
    expectedVersion: input.expectedVersion,
    itemIds: input.itemIds,
    reasonCode: input.reasonCode,
    ...(input.reasonNote ? { reasonNote: input.reasonNote } : {}),
    ...(input.refundDeliveryFee ? { refundDeliveryFee: true as const } : {}),
    ...(returned.length > 0 ? { returnToStockItemIds: returned } : {}),
  };
}

/** The members of `wanted` that are in `itemIds`, in `itemIds`' order, once each. */
function subsetInOrder(
  itemIds: readonly string[],
  wanted: readonly string[],
): string[] {
  if (wanted.length === 0) return [];
  const asked = new Set(wanted);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of itemIds) {
    if (asked.has(id) && !seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

export function nextReasonCodeForQuery(
  t: Translate,
  currentCode: ReasonCode | null,
  query: string,
): ReasonCode | null {
  if (currentCode === null) return null;
  const trimmed = query.trim();
  if (trimmed.length === 0) return null;
  const selectedLabel = voidReasonLabel(t, currentCode).toLowerCase();
  return selectedLabel.includes(trimmed.toLowerCase()) ? currentCode : null;
}
